import { DollarSign, Users, Zap, TrendingUp, Timer, Mail, ShoppingBag, MessageSquare, MousePointerClick, Eye, Clock, Globe, XCircle, PlayCircle, CheckCircle2, Film, RefreshCw, AlertTriangle } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs tracking-[0.2em] text-[#A1B0C6]">{label}</span>
      <Icon className="w-5 h-5 text-[#C9A227]" strokeWidth={1.5} />
    </div>
    <div className="font-display text-3xl sm:text-4xl font-black text-white">{value}</div>
    {sub && <div className="text-xs text-[#A1B0C6] mt-2">{sub}</div>}
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const PKG_SHORT = {
  reply_rescue_5credits: "Rescue Session ($9.99)",
  reply_rescue_15credits: "Deep Rescue ($19.99)",
  reply_rescue_24h_pass: "24h Pass ($14.99)",
};

export const ReplyRescuePanel = ({ stats, onReconcile, reconciling, reconcileResult }) => {
  const s = stats || {};
  return (
    <div className="max-w-7xl mx-auto space-y-8" dir="ltr" data-testid="reply-rescue-panel">
      {/* Payment Reconciliation — same pattern as IPO/Course panels.
          Bulk-queries Stripe for every Pending order in Reply Rescue and
          flips silently-paid ones to Paid (refilling credits + firing
          TikTok Purchase + email). Surfaces decline codes for the rest. */}
      {onReconcile && (
        <div className="bg-[#0B1D3A]/70 border border-[#C9A227]/40 rounded-sm p-5 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between" data-testid="reply-reconcile-bar">
          <div className="flex-1">
            <div className="text-[11px] tracking-[0.22em] text-[#C9A227] font-bold mb-1">PAYMENT RECONCILIATION</div>
            <div className="text-[13px] text-[#A1B0C6] leading-relaxed">
              Asks Stripe directly about every Pending Reply Rescue order — recovers anything where the webhook never arrived (refilling credits + firing email + TikTok Purchase) and surfaces decline reasons for the rest.
            </div>
            {reconcileResult && (
              <div className="mt-2 text-[12.5px] text-[#E7C98C]" data-testid="reply-reconcile-result">
                Checked <span className="font-bold">{reconcileResult.summary?.total || 0}</span> · Fixed <span className="font-bold text-[#5EEAD4]">{(reconcileResult.fixed_orders || []).length}</span> · Silent paid <span className="font-bold">{reconcileResult.summary?.succeeded || 0}</span> · Awaiting card <span className="font-bold">{reconcileResult.summary?.requires_payment_method || 0}</span> · Canceled <span className="font-bold">{reconcileResult.summary?.canceled || 0}</span>
              </div>
            )}
          </div>
          <button
            onClick={onReconcile}
            disabled={reconciling}
            data-testid="reply-reconcile-btn"
            className="bg-gradient-to-b from-[#E2C281] to-[#C9A227] hover:from-[#EBCD93] hover:to-[#b78d1b] text-[#0A0F1C] font-extrabold text-[13px] tracking-wide px-5 py-3 rounded-sm transition-all flex items-center justify-center gap-2 min-h-[44px] disabled:opacity-60 shadow-[0_10px_30px_-12px_rgba(201,162,39,0.6)] shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${reconciling ? "animate-spin" : ""}`} />
            {reconciling ? "Revising with Stripe…" : "Revise payments with Stripe"}
          </button>
        </div>
      )}

      {reconcileResult?.decline_codes?.length > 0 && (
        <div className="bg-[#0B1D3A]/70 border border-[#F87171]/25 rounded-sm p-5" data-testid="reply-decline-codes">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[#FCA5A5]" />
            <h3 className="text-[13px] font-bold tracking-wider text-white">DECLINE CODES (last reconcile)</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {reconcileResult.decline_codes.map(([code, count]) => (
              <span key={code} className="text-[12px] bg-[#F87171]/[0.1] border border-[#F87171]/30 text-[#FCA5A5] px-2.5 py-1 rounded-sm">
                <span className="font-mono">{code}</span> <span className="font-bold text-white">×{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi icon={DollarSign} label="REVENUE" value={`$${(s.revenue_usd || 0).toFixed(2)}`} sub={`${s.payments_count || 0} payments`} testid="reply-stat-revenue" />
        <Kpi icon={Users} label="SESSIONS" value={s.sessions_total || 0} sub={`${s.sessions_active || 0} active (credits or pass)`} testid="reply-stat-sessions" />
        <Kpi icon={Zap} label="CREDITS" value={`${s.credits_used || 0} / ${s.credits_purchased || 0}`} sub="used / purchased" testid="reply-stat-credits" />
        <Kpi icon={TrendingUp} label="REPLIES GENERATED" value={s.replies_generated || 0} sub={`${s.passes_sold || 0} passes sold · ${s.active_passes || 0} active`} testid="reply-stat-replies" />
      </div>

      {/* Funnel KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi icon={Eye} label="PAGE VISITS" value={s.visits || 0} sub="landing page views" testid="reply-stat-visits" />
        <Kpi icon={MousePointerClick} label="CTA CLICKS" value={s.cta_clicks || 0} sub={`CTR ${s.ctr_pct || 0}% of visits`} testid="reply-stat-cta" />
        <Kpi icon={Clock} label="AVG TIME ON SITE" value={`${Math.floor((s.avg_time_on_site_sec || 0) / 60)}m ${(s.avg_time_on_site_sec || 0) % 60}s`} sub="per landing visit" testid="reply-stat-tos" />
        <Kpi icon={ShoppingBag} label="CHECKOUT CONVERSION" value={`${s.checkout_conversion_pct || 0}%`} sub={`${s.checkouts_paid || 0} paid / ${s.checkouts_created || 0} started`} testid="reply-stat-conversion" />
      </div>

      {/* Video walkthrough engagement */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi icon={PlayCircle} label="VIDEO PLAYS" value={s.video_plays || 0} sub="walkthrough started" testid="reply-stat-video-plays" />
        <Kpi icon={CheckCircle2} label="VIDEO COMPLETES" value={s.video_completes || 0} sub="watched to the end" testid="reply-stat-video-completes" />
        <Kpi icon={Film} label="COMPLETION %" value={`${s.video_completion_pct || 0}%`} sub="completes / plays" testid="reply-stat-video-completion" />
        <Kpi icon={TrendingUp} label="VIDEO → CTA" value={`${s.visits ? Math.round((s.video_plays || 0) / s.visits * 100) : 0}%`} sub="plays / visits" testid="reply-stat-video-engagement" />
      </div>

      {/* Countries */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-6" data-testid="reply-payment-countries">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#C9A227]" />
            <h2 className="text-sm font-bold tracking-wider text-white">PAYMENTS BY COUNTRY</h2>
          </div>
          {(s.payment_countries || []).length === 0 ? (
            <div className="text-sm text-[#A1B0C6]">No payments yet.</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {(s.payment_countries || []).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold">{c.country || "Unknown"}</span>
                  <span className="text-[#A1B0C6]">{c.count} payment{c.count > 1 ? "s" : ""} · <span className="text-[#C9A227] font-bold">${(c.revenue_usd || 0).toFixed(2)}</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-6" data-testid="reply-visit-countries">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#5EEAD4]" />
            <h2 className="text-sm font-bold tracking-wider text-white">VISITS BY COUNTRY</h2>
          </div>
          {(s.visit_countries || []).length === 0 ? (
            <div className="text-sm text-[#A1B0C6]">No tracked visits yet.</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {(s.visit_countries || []).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold">{c.country}</span>
                  <span className="text-[#A1B0C6]">{c.count} visit{c.count > 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Unpaid / failed checkout attempts */}
      <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-6" data-testid="reply-unpaid-attempts">
        <div className="flex items-center gap-2 mb-4">
          <XCircle className="w-4 h-4 text-[#F87171]" />
          <h2 className="text-sm font-bold tracking-wider text-white">PAYMENTS NOT PROCESSED (LAST {(s.unpaid_attempts || []).length})</h2>
        </div>
        {(s.unpaid_attempts || []).length === 0 ? (
          <div className="text-sm text-[#A1B0C6]">No unpaid checkout attempts.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] tracking-wider text-[#A1B0C6] border-b border-white/10">
                <th className="py-2 pr-3">WHEN</th>
                <th className="py-2 pr-3">PACKAGE</th>
                <th className="py-2 pr-3">COUNTRY</th>
                <th className="py-2 pr-3">STATUS</th>
                <th className="py-2">REASON</th>
              </tr>
            </thead>
            <tbody>
              {(s.unpaid_attempts || []).map((a, i) => (
                <tr key={i} className="border-b border-white/5 align-top">
                  <td className="py-2.5 pr-3 text-[#A1B0C6] whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="py-2.5 pr-3 text-white">{PKG_SHORT[a.package] || a.package}</td>
                  <td className="py-2.5 pr-3 text-white">{a.country || "—"}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      a.stripe_status === "expired" || a.status === "expired"
                        ? "bg-[#F87171]/15 text-[#FCA5A5]"
                        : "bg-[#FBBF24]/15 text-[#FDE68A]"
                    }`}>{a.stripe_status || a.status || "created"}</span>
                  </td>
                  <td className="py-2.5 text-[#A1B0C6] text-[12px]">{a.reason || "Not checked yet"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Package breakdown */}
      <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-6" data-testid="reply-package-breakdown">
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag className="w-4 h-4 text-[#C9A227]" />
          <h2 className="text-sm font-bold tracking-wider text-white">PACKAGE BREAKDOWN</h2>
        </div>
        {(s.package_breakdown || []).length === 0 ? (
          <div className="text-sm text-[#A1B0C6]">No payments yet.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] tracking-wider text-[#A1B0C6] border-b border-white/10">
                <th className="py-2 pr-4">PACKAGE</th>
                <th className="py-2 pr-4">SALES</th>
                <th className="py-2">REVENUE</th>
              </tr>
            </thead>
            <tbody>
              {(s.package_breakdown || []).map((p) => (
                <tr key={p.package} className="border-b border-white/5">
                  <td className="py-2.5 pr-4 text-white">{PKG_SHORT[p.package] || p.name}</td>
                  <td className="py-2.5 pr-4 text-white font-bold">{p.count}</td>
                  <td className="py-2.5 text-[#C9A227] font-bold">${(p.revenue_usd || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Captured emails */}
        <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-6" data-testid="reply-emails-list">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-4 h-4 text-[#5EEAD4]" />
            <h2 className="text-sm font-bold tracking-wider text-white">CAPTURED EMAILS ({s.emails_total || 0})</h2>
          </div>
          {(s.emails || []).length === 0 ? (
            <div className="text-sm text-[#A1B0C6]">No emails captured yet.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {(s.emails || []).map((e, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white">{e.email}</span>
                  <span className="text-[11px] text-[#A1B0C6]">{fmtDate(e.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent payments */}
        <div className="bg-[#0B1D3A]/70 border border-[#C5A059]/20 rounded-sm p-6" data-testid="reply-recent-payments">
          <div className="flex items-center gap-2 mb-4">
            <Timer className="w-4 h-4 text-[#C9A227]" />
            <h2 className="text-sm font-bold tracking-wider text-white">RECENT PAYMENTS</h2>
          </div>
          {(s.recent_payments || []).length === 0 ? (
            <div className="text-sm text-[#A1B0C6]">No payments yet.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {(s.recent_payments || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <div>
                    <div className="text-white">{PKG_SHORT[p.package_type] || p.package_type}</div>
                    <div className="text-[11px] text-[#A1B0C6]">{fmtDate(p.created_at)}</div>
                  </div>
                  <span className="text-[#C9A227] font-bold">${((p.amount_paid || 0) / 100).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="text-[11px] text-[#A1B0C6] flex items-center gap-2">
        <MessageSquare className="w-3.5 h-3.5" />
        Reply Rescue · replyrescue.app · $9.99 / $19.99 / $14.99 · Stripe Checkout (LIVE)
      </div>
    </div>
  );
};
